import React from 'react';
import { useAuth } from '../context/AuthContext';
import { useForm } from '@mantine/form';
import { TextInput, Button, Paper, Text } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Lock, Mail } from 'lucide-react';

const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const { resetPassword } = useAuth();

  const form = useForm({
    initialValues: {
      email: '',
    },
    validate: {
      email: (value) => (value.length < 5 ? 'Invalid email' : null),
    },
  });

  const handleSubmit = async (values: typeof form.values) => {
    const { error } = await resetPassword(values.email);

    if (error) {
      showNotification({
        title: 'Reset Password Error',
        message: error,
        color: 'red',
      });
      return;
    }

    showNotification({
      title: 'Reset Email Sent',
      message: 'Check your inbox for reset instructions.',
      color: 'green',
    });
  };

  return (
    <div className="py-12 px-4 sm:px-6 lg:px-8 flex flex-col justify-center">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <h1 className="mt-6 text-2xl font-bold text-gray-900">
          Forgot Your Password?
          </h1>
          <p className="mt-2 text-gray-600">
            Enter your email address for further instructions
          </p>
        </div>
      </div>
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-lg sm:rounded-2xl sm:px-10 border border-gray-100">
          <form className="space-y-6" onSubmit={form.onSubmit(handleSubmit)}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  {...form.getInputProps('email')}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="you@example.com"
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 font-medium"
              >
                Send Reset Instructions
              </button>
            </div>
          </form>
        </div>

        <div className="mt-6 text-center">
          <p className="text-gray-600">
            Don't have an account?
            <button
              type="button"
              onClick={() => navigate('/register')}
              className="ml-2 font-medium text-indigo-600 hover:text-indigo-500 focus:outline-none"
            >
              Register an Account
            </button>
          </p>
        </div>
        
        <div className="mt-4 text-center">
          <Link to="/login" className="inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-500">
            <ArrowLeft className="mr-1 h-4 w-4" />
            Back to login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
